import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Radio, Row, Space, Upload } from 'antd';
import axios from 'axios';
import moment from 'moment';

import { useTranslation } from 'react-i18next';
import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import ButtonComponent from '@/components/Button';
import SelectFetching from '@/components/SelectFetching';
import { BATCH_MAX_FILE_SIZE, SEND_USING_FILES_SEND_TYPES } from '@/constants';
import { notificationUtil } from '@/utils';
import { DirectUploadService, SmsManagementService } from '@/services';
import { ReactComponent as FileUploadIcon } from '@/assets/images/icons/file-upload.svg';
import DatePickerComponent from '@/components/DatePicker';
import { validateScheduleCampaignTime } from '@/utils/validate';

import './import-file.scss';
import { getCurrentCountry } from '@/utils/domain';

const { Dragger } = Upload;
const MINUTE_STEP = 5;

function ImportFile({ data, onSubmit, triggerValidateForm }) {
  const [smsTemplateListOptions, setSmsTemplateListOptions] = useState([]);
  const [fileS3Path, setFileS3Path] = useState(() => data?.filePath);
  const [canSubmitForm, setCanSubmitForm] = useState(false);
  const fileTypeAllowed = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  const { t } = useTranslation();
  const [sendTypeValue, setSendTypeValue] = useState(data.sendType);
  const [formInstance] = Form.useForm();

  // get the current Country
  const country = getCurrentCountry();

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.file?.originFileObj;
  };

  useEffect(() => {
    if (fileS3Path) {
      setCanSubmitForm(true);
    }
  }, [fileS3Path]);

  useEffect(() => {
    if (triggerValidateForm) {
      formInstance.submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidateForm]);

  const handleCustomRequest = async ({ onSuccess, file, onError }) => {
    const payload = [
      {
        filename: file.name,
        contentType: file.type,
      },
    ];
    const { success, data: dataRes } = await DirectUploadService.getSignature(
      payload,
    );
    if (success) {
      const { presignedUrl, filename } = dataRes;
      const options = {
        headers: { 'Content-Type': file.type },
      };
      const res = await axios.put(presignedUrl, file, options);
      if (res.status === 200) {
        setFileS3Path(filename);
        onSuccess('ok');
        return;
      }
    }
    onError();
    notificationUtil.setErrorMessage({
      title: t('sms_create_new_request.upload_file'),
      description: t('sms_create_new_request.cannot_upload_file'),
    });
  };

  const fetchSenderList = async (name) => {
    const res = await SmsManagementService.fetchActiveSenderList({ name });

    if (res.success) {
      return res.data.map((sender) => ({
        label: sender.senderName,
        value: sender.id,
      }));
    }
    return [];
  };

  const fetchSmsTemplateList = async () => {
    const res = await SmsManagementService.fetchActiveSmsTemplateList();

    if (res.success) {
      const results = res.data.map((template) => ({
        label: template.name,
        value: template.id,
      }));

      setSmsTemplateListOptions(results);
    }
  };

  useEffect(() => {
    fetchSmsTemplateList();
  }, []);

  const handleFinishForm = (values) => {
    const params = { ...values, fileS3Path };
    onSubmit(params);
  };

  const handleBeforeUpload = (file) => {
    const isValidFile = fileTypeAllowed.includes(file.type);
    if (!isValidFile) {
      notificationUtil.setErrorMessage({
        title: t('sms_create_new_request.upload_file'),
        description: t('sms_create_new_request.invalid_file'),
      });
    }

    const isLtMFS = file.size / 1024 / 1024 < BATCH_MAX_FILE_SIZE;

    if (!isLtMFS) {
      notificationUtil.setErrorMessage({
        title: t('sms_create_new_request.upload_file'),
        description: `File must smaller than ${BATCH_MAX_FILE_SIZE}MB!`,
      });
    }
    return (isValidFile && isLtMFS) || Upload.LIST_IGNORE;
  };

  const handleOnRemoveFile = () => {
    setFileS3Path();
  };

  const handleOnChangeSendType = (e) => {
    const { value } = e.target;
    setSendTypeValue(value);
    if (value === SEND_USING_FILES_SEND_TYPES.sendNow) {
      formInstance.resetFields(['scheduleAt']);
    }
  };

  const range = (start, end) => {
    const result = [];
    for (let i = start; i < end; i += 1) {
      result.push(i);
    }
    return result;
  };

  const disabledDateTime = (date) => {
    if (!date) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
      };
    }
    const currentSelectedDate = date.clone();

    return {
      disabledHours: () => {
        let maxHourSelect = moment().hour();
        if (currentSelectedDate.startOf('day') > moment().startOf('day')) {
          maxHourSelect = 0;
        } else if (moment().minute() >= (60 / MINUTE_STEP - 1) * MINUTE_STEP) {
          maxHourSelect += 1;
        }
        return range(0, maxHourSelect);
      },
      disabledMinutes: (selectedHour) => {
        const currentHour = moment().hour();
        const currentMinute = moment().minute();
        // small 0.01 value for case current minute eq 0, 15, 30, 45
        let maxMinuteSelect =
          Math.ceil((currentMinute + 0.01) / MINUTE_STEP) * MINUTE_STEP;
        if (
          currentSelectedDate.startOf('day') > moment().startOf('day') ||
          selectedHour > currentHour ||
          currentMinute >= (60 / MINUTE_STEP - 1) * MINUTE_STEP
        ) {
          maxMinuteSelect = 0;
        } else if (selectedHour === currentHour) {
          maxMinuteSelect =
            Math.ceil((currentMinute + 0.01) / MINUTE_STEP) * MINUTE_STEP;
        }
        return range(0, maxMinuteSelect);
      },
    };
  };

  return (
    <div className="import-file-container">
      <Form
        layout="vertical"
        onFinish={handleFinishForm}
        autoComplete="off"
        className="form"
        initialValues={data}
        form={formInstance}
      >
        <Form.Item
          label={t('sms_create_new_request.description')}
          name="description"
          rules={[
            {
              required: true,
              message: t('sms_create_new_request.error_code.description'),
            },
          ]}
        >
          <InputComponent
            placeholder={t('sms_create_new_request.placeholder.description')}
          />
        </Form.Item>
        <Row gutter={[12, 12]}>
          {country !== 'br' && (
            <Col span={12}>
              <Form.Item
                label={t('sms_page.tabs.third')}
                name="senderId"
                className="form-item"
                rules={[
                  {
                    required: true,
                    message: t(
                      'sms_page.quick_send.form.sender_name.required_msg',
                    ),
                  },
                ]}
              >
                <SelectFetching
                  allowClear
                  triggerFetching
                  placeholder={t(
                    'sms_page.quick_send.form.sender_name.placeholder',
                  )}
                  fetchOptions={fetchSenderList}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={country === 'br' ? 24 : 12}>
            <Form.Item
              label={t('sms_page.tabs.fourth')}
              name="templateId"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t('sms_page.tabs.fourth')}
                options={smsTemplateListOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="mb-12">
          <Col span={12}>
            <Form.Item
              name="sendType"
              label={t('sms_create_new_request.schedule_send_sms')}
            >
              <Radio.Group
                value={sendTypeValue}
                onChange={handleOnChangeSendType}
              >
                <Space direction="vertical">
                  <Radio value={SEND_USING_FILES_SEND_TYPES.sendNow}>
                    {t(
                      `sms_create_new_request.send_types.${SEND_USING_FILES_SEND_TYPES.sendNow}`,
                    )}
                  </Radio>
                  <Radio value={SEND_USING_FILES_SEND_TYPES.scheduleCampaign}>
                    {t(
                      `sms_create_new_request.send_types.${SEND_USING_FILES_SEND_TYPES.scheduleCampaign}`,
                    )}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {sendTypeValue === SEND_USING_FILES_SEND_TYPES.scheduleCampaign && (
              <Form.Item
                label=""
                name="scheduleAt"
                rules={[
                  {
                    required: true,
                    message: t(
                      'sms_create_new_request.error_code.schedule_at.required',
                    ),
                  },
                  () => ({
                    validator(_, value) {
                      if (value && !validateScheduleCampaignTime(value)) {
                        return Promise.reject(
                          new Error(
                            t(
                              'sms_create_new_request.error_code.schedule_at.invalid',
                            ),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePickerComponent
                  showTime={{
                    minuteStep: MINUTE_STEP,
                    format: 'HH:mm',
                    hideDisabledOptions: true,
                  }}
                  className="mt-12 w-100"
                  showNow={false}
                  inputReadOnly
                  format="MMM DD, YYYY HH:mm"
                  placeholder={t(
                    'sms_create_new_request.placeholder.schedule_time',
                  )}
                  disabledDate={(currentDate) =>
                    currentDate < moment().startOf('day')
                  }
                  disabledTime={disabledDateTime}
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Form.Item
          name="file"
          valuePropName="file"
          rules={[
            {
              required: true,
              message: t('sms_create_new_request.error_code.file'),
            },
          ]}
          getValueFromEvent={normFile}
        >
          <Dragger
            maxCount={1}
            multiple={false}
            className="upload-file"
            accept=".xls, .csv, .xlsx"
            customRequest={handleCustomRequest}
            defaultFileList={data?.file ? [data.file] : []}
            beforeUpload={handleBeforeUpload}
            onRemove={handleOnRemoveFile}
          >
            <p className="ant-upload-drag-icon">
              <FileUploadIcon />
            </p>
            <p className="ant-upload-text">
              {t('sms_create_new_request.upload_a_file')}
            </p>
            <div className="ant-upload-hint">
              <p>{t('sms_create_new_request.error_code.column')}</p>
              <p className="support-files">
                {t('sms_create_new_request.error_code.only_file')}
              </p>
            </div>
          </Dragger>
        </Form.Item>

        <div className="sample-files">
          <div>{t('sms_create_new_request.error_code.download_file')}</div>
          <div className="download-sample-files">
            <ButtonComponent type="black">
              <a
                href="/sample-files/example_EXCEL_XLSX.xlsx"
                download="example_EXCEL_XLS.xls"
              >
                XLS
              </a>
            </ButtonComponent>
            <ButtonComponent type="black">
              <a
                href="/sample-files/example_CSV_UTF8.csv"
                download="example_CSV_UTF8.csv"
              >
                CSV
              </a>
            </ButtonComponent>
            <ButtonComponent type="black">
              <a
                href="/sample-files/example_EXCEL_XLSX.xlsx"
                download="example_EXCEL_XLSX.xlsx"
              >
                XLSX
              </a>
            </ButtonComponent>
          </div>
          <div className="support-files">
            {t('sms_create_new_request.error_code.extensions')}
          </div>
        </div>

        <Form.Item>
          <div className="form-actions-item">
            {/* <ButtonComponent
              type="default"
            >
              Schedule Campaign
            </ButtonComponent> */}
            <ButtonComponent
              type="primary"
              htmlType="submit"
              disabled={!canSubmitForm}
            >
              {t('sms_create_new_request.next')}
            </ButtonComponent>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}

ImportFile.propTypes = {
  data: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  triggerValidateForm: PropTypes.bool,
};

export default ImportFile;
